import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ContractFormFields } from 'page/CustomerPage/types/contractFormField';
import {
  ContractRegistrationStep,
  CONTRACT_REGISTRATION_STEP,
} from 'page/CustomerPage/types/contractRegistrationStep';

import type { RootState } from 'store';
import { convertUTCStringIntoJSTString, DATE_FORMAT } from 'utils/dateUtils';

/**
 * 契約情報の登録画面の状態
 */
interface ContractRegistrationState {
  /**
   * 登録モーダル表示フラグ
   */
  isOpen: boolean;
  /**
   * 登録のステップ
   */
  step: ContractRegistrationStep;
  /**
   * 契約メニュー ID
   */
  menuId: number | undefined;
  /**
   * 契約開始日
   */
  startDate: string;
  /**
   * 契約終了日
   */
  endDate: string;
}

/**
 * 契約情報登録画面の初期状態
 */
const initialState: ContractRegistrationState = {
  isOpen: false,
  step: CONTRACT_REGISTRATION_STEP.INPUT,
  menuId: undefined,
  startDate: '',
  endDate: '2100-12-30T15:00:00.000Z',
};

/**
 * 顧客情報登録画面の slice(reducer + action)
 */
export const contractRegistrationSlice = createSlice({
  name: 'contractRegistration',
  initialState,
  reducers: {
    init: () => initialState,
    modalOpened: (state) => {
      state.isOpen = true;
    },
    stepMovedToConfirmation: (state) => {
      state.step = CONTRACT_REGISTRATION_STEP.CONFIRMATION;
    },
    stepMovedToInput: (state) => {
      state.step = CONTRACT_REGISTRATION_STEP.INPUT;
    },
    formGroupUpdated: (
      state,
      action: PayloadAction<{
        form: ContractFormFields;
      }>,
    ) => {
      const { menuId } = action.payload.form;
      state.menuId = menuId ? parseInt(menuId, 10) : undefined;
    },
    startDateChanged: (
      state,
      action: PayloadAction<{
        startDate: string;
      }>,
    ) => {
      state.startDate = action.payload.startDate;
    },
    endDateChanged: (
      state,
      action: PayloadAction<{
        endDate: string;
      }>,
    ) => {
      state.endDate = action.payload.endDate;
    },
  },
});

/**
 * 契約情報登録画面の action
 */
export const contractRegistrationActions = contractRegistrationSlice.actions;

/**
 * 契約情報登録画面の selectors
 */
export const contractRegistrationSelectors = {
  isOpenInputModal: (state: RootState) =>
    state.customer.contractRegistration.isOpen &&
    state.customer.contractRegistration.step ===
      CONTRACT_REGISTRATION_STEP.INPUT,
  isOpenConfirmationModal: (state: RootState) =>
    state.customer.contractRegistration.isOpen &&
    state.customer.contractRegistration.step ===
      CONTRACT_REGISTRATION_STEP.CONFIRMATION,
  menuId: (state: RootState) => state.customer.contractRegistration.menuId,
  startDate: (state: RootState) =>
    state.customer.contractRegistration.startDate,
  displayStartDate: (state: RootState) => {
    const date = state.customer.contractRegistration.startDate;

    return date ? convertUTCStringIntoJSTString(date, DATE_FORMAT.DATE) : '';
  },
  endDate: (state: RootState) => state.customer.contractRegistration.endDate,
  displayEndDate: (state: RootState) => {
    const date = state.customer.contractRegistration.endDate;

    return date ? convertUTCStringIntoJSTString(date, DATE_FORMAT.DATE) : '';
  },
} as const;
